import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { AnnouncementService } from '../services/announcements'
import { AnnouncementReadsService } from '../services/announcementReads'
import { SchoolAnnouncementService, filterSchoolAnnouncementsForStudent } from '../services/schoolAnnouncements'
import { GroupService } from '../services/groups'
import { AuthService } from '../services/auth'
import { useAdminSelectedSchool } from '../utils/adminSchoolContext'
import type { Announcement } from '../services/announcements'
import type { SchoolAnnouncement } from '../services/schoolAnnouncements'
import Layout from '../components/Layout'
import announcementIconMark from '../assets/icon/announcement-icon-mark.svg'

const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [schoolAnnouncements, setSchoolAnnouncements] = useState<SchoolAnnouncement[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const location = useLocation()
  const adminSelectedSchool = useAdminSelectedSchool()

  useEffect(() => {
    loadAnnouncements()
  }, [adminSelectedSchool]) // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to anchor after announcements are loaded
  useEffect(() => {
    if (!isLoading && (announcements.length > 0 || schoolAnnouncements.length > 0) && location.hash) {
      const targetId = location.hash.substring(1)
      const targetElement = document.getElementById(targetId)
      if (targetElement) {
        setTimeout(() => {
          const headerOffset = 80
          const elementPosition = targetElement.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
        }, 100)
      }
    }
  }, [isLoading, announcements.length, schoolAnnouncements.length, location.hash])

  const loadAnnouncements = async () => {
    try {
      setIsLoading(true)
      const [systemData, user] = await Promise.all([
        AnnouncementService.getPublishedAnnouncements(),
        AuthService.getCurrentUser(),
      ])
      setAnnouncements(systemData)

      const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin'
      const effectiveSchoolId = isAdminUser ? (adminSelectedSchool?.id ?? null) : user?.school_id

      if (user && effectiveSchoolId) {
        const rawSchool = await SchoolAnnouncementService.getPublishedSchoolAnnouncements(effectiveSchoolId)
        if (isAdminUser) {
          // An admin has no grade, class, or groups in the school they're viewing, so the
          // student filter would hide every targeted announcement. Show the school's full set.
          setSchoolAnnouncements(rawSchool)
        } else {
          let studentGroupIds: string[] = []
          try {
            const studentGroups = await GroupService.getStudentGroups(user.id)
            studentGroupIds = studentGroups.map(g => g.id)
          } catch {
            // Student may not belong to any groups or policy not yet applied
          }
          const filtered = filterSchoolAnnouncementsForStudent(
            rawSchool,
            user.grade ?? null,
            user.class ?? null,
            studentGroupIds
          )
          setSchoolAnnouncements(filtered)
        }
      } else {
        // No school in scope (admin on すべての学校, or a user without one) — drop any
        // announcements left over from a previously selected school.
        setSchoolAnnouncements([])
      }
      try { await AnnouncementReadsService.markAllRead() } catch { /* non-critical */ }
    } catch (error) {
      console.error('Failed to load announcements:', error)
      setError('お知らせの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const renderAnnouncementCard = (
    announcement: Announcement | SchoolAnnouncement,
    isSchool: boolean
  ) => (
    <div
      key={announcement.id}
      id={announcement.id}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 flex-1">
            {isSchool ? (
              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700 whitespace-nowrap">
                学校
              </span>
            ) : (
              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">
                MiraPASS
              </span>
            )}
            <h2 className="text-xl font-semibold text-gray-900">{announcement.title}</h2>
          </div>
          <time className="text-sm text-gray-500 ml-4 whitespace-nowrap">
            {formatDate(announcement.published_at)}
          </time>
        </div>

        {announcement.image_path && (
          <div className="mb-4">
            <img
              src={isSchool
                ? SchoolAnnouncementService.getImageUrl(announcement.image_path)
                : AnnouncementService.getImageUrl(announcement.image_path)
              }
              alt=""
              className="w-full max-w-md object-contain rounded-lg border border-gray-200"
            />
          </div>
        )}

        <div className="prose prose-sm max-w-none mb-4">
          <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
        </div>

        {announcement.url && (
          <div className="border-t border-gray-100 pt-4">
            <a
              href={announcement.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              詳細を見る
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  )

  return (
    // Layout paints the same rainbow gradient the other student pages sit on.
    <Layout>
      {/* max-w-7xl + the header's own padding keeps this column flush with the header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
        {/* Filled white panel on top of the gradient */}
        <div className="bg-white rounded-2xl shadow-lg px-4 pb-4 pt-3 sm:px-6 sm:pb-6 sm:pt-3">
          {/* 提出済みの宿題 と同じ大きさのアイコン＋見出し */}
          <h1 className="flex items-center gap-2 mb-3">
            <img alt="" className="h-10 w-[45px] shrink-0" src={announcementIconMark} />
            <span className="text-xl font-bold text-[#33333b]">お知らせ</span>
          </h1>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <div className="text-red-800 text-sm">{error}</div>
              <button
                onClick={() => {
                  setError('')
                  loadAnnouncements()
                }}
                className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
              >
                再試行
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#da4574]"></div>
              <span className="ml-3 text-gray-600">読み込み中...</span>
            </div>
          ) : announcements.length === 0 && schoolAnnouncements.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">現在お知らせはありません</div>
            </div>
          ) : (
            <div className="space-y-6">
              {[
                ...announcements.map(a => ({ ann: a, isSchool: false })),
                ...schoolAnnouncements.map(a => ({ ann: a, isSchool: true })),
              ]
                .sort((a, b) => new Date(b.ann.published_at).getTime() - new Date(a.ann.published_at).getTime())
                .map(({ ann, isSchool }) => renderAnnouncementCard(ann, isSchool))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default AnnouncementsPage
